import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import MSAModulePage from './MSAModulePage';

// ---------------------------------------------------------------------------
// CapabilityImpactModule — Measurement Capability Impact (studyType
// "capabilityImpact"). Decomposes observed process variance into measurement
// error (from a GRR study) vs. true process variation, and shows the capability
// (Cp/Cpk) that a perfect gauge would reveal.
//
// This is a simple form (no grid): required σ²_GRR and observed σ, plus optional
// spec limits (USL/LSL) and process mean to unlock Cp/Cpk.
// ---------------------------------------------------------------------------

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: 'rgba(255, 255, 255, 0.9)',
        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(144, 202, 249, 0.7)' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(144, 202, 249, 0.9)' },
    '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.4)' },
};

// Blank ('' / undefined) → undefined so the backend's optional branches fire.
function optionalNumber(raw) {
    if (raw === undefined || raw === null) return undefined;
    const s = String(raw).trim();
    if (s === '') return undefined;
    const n = Number(s);
    if (!isFinite(n)) throw new Error('Optional fields must be valid numbers when provided.');
    return n;
}

function requiredNonNegative(raw, label) {
    const s = raw === undefined || raw === null ? '' : String(raw).trim();
    const n = Number(s);
    if (s === '' || !isFinite(n) || n < 0) {
        throw new Error(`${label} is required and must be a non-negative number.`);
    }
    return n;
}

export const CapabilityImpactInputs = forwardRef(function CapabilityImpactInputs({ disabled }, ref) {
    const [grrVariance, setGrrVariance]       = useState('');
    const [observedStdDev, setObservedStdDev] = useState('');
    const [usl, setUsl]                       = useState('');
    const [lsl, setLsl]                       = useState('');
    const [mean, setMean]                     = useState('');

    useImperativeHandle(ref, () => ({
        buildInputs() {
            const gv = requiredNonNegative(grrVariance, 'GRR Variance');
            const os = requiredNonNegative(observedStdDev, 'Observed Process Std Dev');

            const uslN  = optionalNumber(usl);
            const lslN  = optionalNumber(lsl);
            const meanN = optionalNumber(mean);

            if (uslN !== undefined && lslN !== undefined && uslN <= lslN) {
                throw new Error('USL must be greater than LSL.');
            }

            return {
                grrVariance: gv,
                observedStdDev: os,
                usl: uslN,
                lsl: lslN,
                mean: meanN,
            };
        },
        // Re-populate the form from a persisted engine input (history reload).
        hydrate(input) {
            if (!input || typeof input !== 'object') return;
            const s = (v) => (v === undefined || v === null ? '' : String(v));
            setGrrVariance(s(input.grrVariance));
            setObservedStdDev(s(input.observedStdDev));
            setUsl(s(input.usl));
            setLsl(s(input.lsl));
            setMean(s(input.mean));
        },
    }), [grrVariance, observedStdDev, usl, lsl, mean]);

    const numericProps = { inputMode: 'decimal' };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>
                Enter the measurement variance from a GRR study and the observed process
                spread. Add spec limits (and a process mean for Cpk) to see the capability a
                perfect gauge would reveal.
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2,
                }}
            >
                <TextField
                    label="GRR Variance (σ²_GRR)"
                    required
                    size="small"
                    fullWidth
                    sx={inputSx}
                    value={grrVariance}
                    onChange={e => setGrrVariance(e.target.value)}
                    disabled={disabled}
                    inputProps={numericProps}
                    helperText="Variance from a GRR study = GRR²."
                    placeholder="e.g. 0.0025"
                />
                <TextField
                    label="Observed Process Std Dev (σ)"
                    required
                    size="small"
                    fullWidth
                    sx={inputSx}
                    value={observedStdDev}
                    onChange={e => setObservedStdDev(e.target.value)}
                    disabled={disabled}
                    inputProps={numericProps}
                    placeholder="e.g. 0.075"
                />
                <TextField
                    label="USL"
                    size="small"
                    fullWidth
                    sx={inputSx}
                    value={usl}
                    onChange={e => setUsl(e.target.value)}
                    disabled={disabled}
                    inputProps={numericProps}
                    placeholder="Upper spec limit (optional)"
                />
                <TextField
                    label="LSL"
                    size="small"
                    fullWidth
                    sx={inputSx}
                    value={lsl}
                    onChange={e => setLsl(e.target.value)}
                    disabled={disabled}
                    inputProps={numericProps}
                    placeholder="Lower spec limit (optional)"
                />
                <TextField
                    label="Process Mean"
                    size="small"
                    fullWidth
                    sx={inputSx}
                    value={mean}
                    onChange={e => setMean(e.target.value)}
                    disabled={disabled}
                    inputProps={numericProps}
                    helperText="Required to compute Cpk."
                    placeholder="Process center (optional)"
                />
            </Box>
        </Box>
    );
});

export default function CapabilityImpactModule({ onBack }) {
    return (
        <MSAModulePage
            studyType="capabilityImpact"
            title="Measurement Capability Impact"
            subtitle="Capability Decomposition"
            onBack={onBack}
            descriptionPlaceholder="e.g. Shaft diameter — impact of gauge error on Cpk"
            helperText="Decompose observed variance into measurement error vs. true process variation."
        >
            {({ inputRef, disabled }) => <CapabilityImpactInputs ref={inputRef} disabled={disabled} />}
        </MSAModulePage>
    );
}
